<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import Select from 'primevue/select';
import { useProductStore } from '@/stores/productsStore';
import { useToast } from 'vue-toastification';

const authStore = useAuthStore();
const orderStore = useOrderStore();
const productStore = useProductStore();
const toast = useToast();

// Load salesPersons
onMounted(async () => {
  await authStore.fetchCashiers();
  console.log('SalesPersons loaded in BarberItemsPopup:', authStore.salesPersons);
});

const handleClose = () => {
  authStore.isBarberItemsPopupVisible = false;
  selectedItems.value = [];
};

//Getbarber Items list in itemslist file
//const barberItemsListFromItemListFile = computed (() => {
//  const ItemsListFromItemListFile = productStore.products.filter(item => item.Name.includes('haircut-template'));
//  console.log('Barber Items List from Item List File:', ItemsListFromItemListFile);
//  return ItemsListFromItemListFile;
//});

// All service items: match specialized group items to full products by Sku
const services = computed(() => {
  const group = productStore.spcializedItems.find(
    g => g.Name === productStore.selectedSpecializedGroup
  );
  if (!group) return [];
  return group.Items.map((groupItem) => {
    const sku = groupItem?.Product?.Sku;
    const matchedProduct = productStore.products.find(p => p.Sku === sku);
    const fallback = groupItem?.Product || {};
    return {
      // Use Sku as stable id to avoid index mismatch
      id: sku || fallback.Sku || Math.random().toString(36).slice(2),
      name: matchedProduct?.Name || fallback.Name || 'Unnamed',
      price: matchedProduct?.Price ?? fallback.Price ?? 0,
      imageUrl: matchedProduct?.ImageUrl || fallback.ImageUrl || '',
      sku,
    };
  });
});


// Each item selected state
const selectedItems = ref([]);
const selectedSalesperson = ref(null);

function toggleSelect(item) {
  const existing = selectedItems.value.find(i => i.id === item.id);
  if (existing) {
    selectedItems.value = selectedItems.value.filter(i => i.id !== item.id);
  } else {
    selectedItems.value.push({ ...item, quantity: 1 });
  }
}

function changeQty(item, amount) {
  const found = selectedItems.value.find(i => i.id === item.id);
  if (!found) return;
  found.quantity = Math.max(0, found.quantity + amount);
  if (found.quantity === 0) {
    selectedItems.value = selectedItems.value.filter(i => i.id !== item.id);
  }
}

function addSelection() {
  if (!orderStore.selectedSalesPerson) {
    toast.error("Please select a salesperson first!");
    return;
  }

  if (selectedItems.value.length === 0) {
    toast.error("Please select at least one item!");
    return;
  }

  let addedItemsCount = 0;

  // Add each selected item to the order
  selectedItems.value.forEach(selectedItem => {
    // Find the full product from products array using Sku (selectedItem.id)
    const product = productStore.products.find(p => p.Sku === selectedItem.id);

    if (product) {
      const orderItemData = {
        Sku: product.Sku,
        RetailSku: product.RetailSku || product.Sku,
        Name: product.Name,
        Price: selectedItem.price ?? product.Price ?? 0,
        Qty: selectedItem.quantity,
        ImageUrl: product.ImageUrl || '',
        Discount: 0,
        SalesPersonId: orderStore.selectedSalesPerson?.SalesPersonId || null
      };

      try {
        orderStore.addOrderItem(orderItemData);
        orderStore.setSelectedSalesPerson(orderStore.selectedSalesPerson, orderItemData);
        addedItemsCount++;
        console.log(`Added item to order: ${orderItemData.Name} (Qty: ${orderItemData.Qty})`);
      } catch (error) {
        console.error(`Failed to add item ${orderItemData.Name}:`, error);
        toast.error(`Failed to add item ${orderItemData.Name}`);
      }
    } else {
      console.warn(`Product not found in products array for Sku/ID: ${selectedItem.id}`);
    }
  });

  if (addedItemsCount > 0) {
    toast.success(`Successfully added ${addedItemsCount} item(s) to order`);
    console.log(`Successfully added ${addedItemsCount} item(s) to order`);
    selectedItems.value = [];
    authStore.isBarberItemsPopupVisible = false;
  } else {
    toast.error("No items were added to the order. Please check your selection and try again.");
  }
}

</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div v-show="authStore.isBarberItemsPopupVisible" @click.self="handleClose" :class="[
        'absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8',
        isRequsetBeingMade ? 'pointer-events-none' : ''
      ]">
        <Transition name="modal-inner" class="rounded-2xl">
          <div v-if="authStore.isBarberItemsPopupVisible"
            class="fixed top-10 z-50 flex items-center justify-center bg-black bg-opacity-50 w-10/12">
            <div class="bg-white rounded-2xl shadow-lg p-6 w-full max-h-[880px] overflow-auto">
              <h1 class="font-semibold text-[24px] pb-4">Service Menu</h1>
              <div class="p-6 rounded-2xl">
                <!-- Salesperson selector -->
                <div class="flex justify-between items-center mb-4">
                  <div class="border-2 rounded-lg px-2">
                    <Select class="w-full py-2 border md:w-56" v-model="orderStore.selectedSalesPerson"
                      :options="[...authStore.salesPersons]" optionLabel="fullName"
                      placeholder="Select a salesperson" />
                  </div>
                  <button @click="addSelection"
                    class="bg-purple-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-purple-700">
                    Add
                  </button>
                </div>

                <!-- Services grid -->
                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  <div
                    v-for="item in services"
                    :key="item.id"
                    @click="toggleSelect(item)"
                    class="cursor-pointer p-4 bg-gray-100 border rounded-xl flex flex-col transition-all duration-200"
                    :class="selectedItems.some(i => i.id === item.id)
                      ? 'bg-purple-100'
                      : 'hover:bg-gray-50'"
                  >
                    <div class="flex gap-2">
                      <div class="text-4xl mb-2">
                        <img class="w-12" :src="item.imageUrl" alt="icon">
                      </div>
                      <div>
                        <p class="text-xl font-bold text-center uppercase">{{ item.name }}</p>
                        <p class="text-3xl font-semibold">${{ item.price }}</p>
                      </div>
                    </div>
                    <div
                      v-if="selectedItems.some(i => i.id === item.id)"
                      class="flex items-center justify-center gap-4 mt-2"
                      @click.stop
                    >
                      <button class="pi pi-minus py-2 px-3 bg-gray-200 rounded" @click="changeQty(item, -1)"></button>
                      <span>{{ selectedItems.find(i => i.id === item.id)?.quantity }}</span>
                      <button class="pi pi-plus py-2 px-3 bg-gray-200 rounded" @click="changeQty(item, 1)"></button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>


<style scoped>
.modal-outer-enter-active,
  .modal-outer-leave-active {
    transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
  }

  .modal-outer-enter-from,
  .modal-outer-leave-to {
    opacity: 0;
  }

  .modal-inner-enter-active {
    transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
  }
  
  .modal-inner-leave-active {
    transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
  }

  .modal-inner-enter-from {
    opacity: 0;
    transform: scale(0.8);
  }

  .modal-inner-leave-to {
    transform: scale(0.8);
  }
</style>